import { type Dispatch, type SetStateAction, useState } from 'react'

import { FvButton } from '@fv/client-components'
import type { FullShipment } from '@fv/client-types'

import InfoBox from '@/components/InfoBox'
import { usePermissions } from '@/features/auth'
import { ShipperOnly } from '@/features/auth/HasRole'
import { useHasPermission, useRole } from '@/features/auth/hooks'
import { AddRateButton } from '@/features/quote/ManualRatePanel'
import { useCancelLoad, useEditBookingPage } from '@/hooks/shipments'
import { useRepeatShipment } from '@/hooks/shipments/useRepeatShipment'
import { useLoadEditRoute, useLoadRatesPage } from '@/route-hooks'
import { routes } from '@/routes'

import { ActionList, ActionListItem } from '../../ActionList'

type Props = {
  isShowingRates: boolean
  load: FullShipment
  setShowingRates: Dispatch<SetStateAction<boolean>>
  setViewingActive: Dispatch<SetStateAction<boolean>>
}

export const PanelFooter = ({
  isShowingRates,
  load,
  setShowingRates,
  setViewingActive,
}: Props) => {
  const [showCancelDialog, setShowCancelDialog] = useState(false)
  const { isMultiTruck, loadId, pickup, status } = load
  const { canViewRates, canBook } = usePermissions()
  const hasCancelPermission = useHasPermission('load-canceler')
  const { canEdit, composeEditLink } = useEditBookingPage(load)
  const editRoute = useLoadEditRoute()
  const ratesPage = useLoadRatesPage()
  const role = useRole()
  const repeat = useRepeatShipment(load)

  const cancelLoad = useCancelLoad(loadId)
  const isCanceling = cancelLoad.isPending || cancelLoad.isSuccess

  const cancelLoads = (isAllLoads: boolean) => {
    if (isCanceling) return

    cancelLoad.mutate(isAllLoads, {
      onSuccess: () => {
        setShowCancelDialog(false)
        setShowingRates(false)
      },
    })
  }

  const hasRepeatButton = !load.isFreightCollect
  const hasBookingError = status === 'awarded' && pickup?.status === 'error'
  const hasCancelButton =
    (status !== 'awarded' || hasBookingError) && hasCancelPermission
  const hasEditLoadButton =
    canEdit && status === 'awarded' && !load.isFreightCollect
  const hasEditRequestButton = status === 'pending' && !load.isFreightCollect
  const hasManualRateButton =
    load.workflow !== 'parcel' &&
    (status !== 'awarded' || isShowingRates) &&
    canViewRates &&
    role !== 'vendor' &&
    !load.isFreightCollect
  const hasFreightCollectButton =
    load.workflow === 'truckload' &&
    load.isFreightCollect &&
    status === 'pending' &&
    !load.selectedQuote &&
    canBook

  if (
    !hasCancelButton &&
    !hasEditLoadButton &&
    !hasEditRequestButton &&
    !hasManualRateButton &&
    !hasFreightCollectButton
  ) {
    return null
  }

  return (
    <>
      <ActionList>
        {hasManualRateButton && (
          <AddRateButton
            icon="plus"
            load={load}
            onSuccess={() => setViewingActive(true)}
            rateType={load.workflow === 'ltl' ? 'manual-rate' : 'spot-quote'}
          >
            Rate
          </AddRateButton>
        )}

        {hasEditLoadButton && (
          <ActionListItem icon="pen-alt" to={composeEditLink()}>
            Edit load
          </ActionListItem>
        )}

        {hasEditRequestButton && (
          <ActionListItem icon="pen-alt" to={editRoute(load, 'edit')}>
            Edit
          </ActionListItem>
        )}
        {hasRepeatButton && (
          <ShipperOnly permissions={['load-creator']}>
            <ActionListItem
              icon="clone"
              onClick={repeat}
              tooltip="Repeat this shipment"
            >
              Repeat
            </ActionListItem>
          </ShipperOnly>
        )}

        {hasCancelButton && (
          <ActionListItem
            icon="times"
            onClick={() => setShowCancelDialog(!showCancelDialog)}
            tooltip="Cancel shipment"
          >
            {load.isFreightCollect ? 'Cancel shipment' : ''}
          </ActionListItem>
        )}
        {hasFreightCollectButton && (
          <ActionListItem
            icon="truck-moving"
            tooltip="Book shipment"
            to={routes.tlCustomerRoutedBook(load.loadId)}
          >
            Book with new carrier
          </ActionListItem>
        )}
        {!hasFreightCollectButton && (
          <ActionListItem icon="list" to={ratesPage(load)} tooltip="Details">
            Details
          </ActionListItem>
        )}
      </ActionList>

      {showCancelDialog && (
        <div className="mt-4">
          <InfoBox>
            {!isMultiTruck && (
              <>
                Cancelling this quote request will remove it from this list and
                lose all of its data. Carriers will be notified that this load
                is no longer available. Proceed?
              </>
            )}

            {isMultiTruck && (
              <>
                This quote request contains multiple loads. Do you just want to
                cancel this specific load or every load for this entire quote
                request? Canceling a load will remove it from this list and lose
                all of its data. Carriers will be notified that these loads are
                no longer available. Proceed?
              </>
            )}
          </InfoBox>
          <div className="mb-2 flex items-center !justify-end">
            <FvButton
              theme="default"
              onClick={() => {
                !isCanceling && setShowCancelDialog(false)
              }}
            >
              No
            </FvButton>

            {isMultiTruck && (
              <FvButton
                theme="primary"
                onClick={() => cancelLoads(false)}
                icon={isCanceling ? 'spinner' : 'check'}
              >
                <span>Yes, this load only</span>
              </FvButton>
            )}

            <FvButton
              theme="primary"
              onClick={() => cancelLoads(true)}
              icon={isCanceling ? 'spinner' : 'check'}
            >
              <span>
                {isMultiTruck
                  ? 'Yes, all loads'
                  : 'Yes, cancel this quote request'}
              </span>
            </FvButton>
          </div>
        </div>
      )}
    </>
  )
}
